import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { User, Token } from '@shared/types';

interface AuthState {
  // State
  user: User | null
  token: Token | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  otpFlowActive: boolean

  // Actions
  setUser: (user: User) => void
  setToken: (token: Token) => void
  setLoading: (loading: boolean) => void
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  updateUser: (updates: Partial<User>) => void
  refreshAccessToken: () => Promise<void>
  clearAuth: () => void
  startOtpFlow: () => void
  endOtpFlow: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      otpFlowActive: false,

      // Actions
      setUser: (user) => {
        set({ user, isAuthenticated: true });
      },

      setToken: (token) => {
        set({
          token,
          isAuthenticated: true,
        });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      login: async(email, password) => {
        try {
          set({ isLoading: true });


        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        // Clear store state
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          otpFlowActive: false,
        });

        // Clear all storage
        if (typeof window !== 'undefined') {
          try {
            localStorage.clear();
          } catch (_e) {}
          try {
            sessionStorage.clear();
          } catch (_e) {}
        }
      },

      clearAuth: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          otpFlowActive: false,
        });
      },

      checkAuth: async() => {
        const { token } = get();

        if (!token) {
          set({ isAuthenticated: false, isLoading: false });
          return;
        }

        try {
          set({ isLoading: true });


        } catch (error) {
          // Token might be expired, try to refresh
          try {
            await get().refreshAccessToken();
          } catch (refreshError) {
            get().logout();
          }
          set({ isLoading: false });
        }
      },

      updateUser: (updates) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } });
        }
      },

      refreshAccessToken: async() => {
        const { refreshToken: currentRefreshToken } = get();

        if (!currentRefreshToken) {
          throw new Error('No refresh token available');
        }

        try {

        } catch (error) {
          get().logout();
          throw error;
        }
      },

      startOtpFlow: () => {
        set({ otpFlowActive: true });
      },

      endOtpFlow: () => {
        set({ otpFlowActive: false });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
